<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch, type ComputedRef, type Ref } from "vue";
import { activeAppDialog, settleAppDialog, type AppDialogRequest } from "../dialogs/app-dialog.js";

const dialogPanel: Ref<HTMLElement | null> = ref(null);
const promptInput: Ref<HTMLInputElement | null> = ref(null);
const cancelButton: Ref<HTMLButtonElement | null> = ref(null);
const inputValue: Ref<string> = ref("");
let previouslyFocusedElement: HTMLElement | null = null;

const dialog: ComputedRef<AppDialogRequest | null> = computed(
  (): AppDialogRequest | null => activeAppDialog.value,
);
const confirmationAllowed: ComputedRef<boolean> = computed((): boolean => {
  const requiredValue: string | null | undefined = dialog.value?.requiredValue;
  return requiredValue === null || requiredValue === undefined || inputValue.value === requiredValue;
});

watch(dialog, async (current: AppDialogRequest | null, previous: AppDialogRequest | null): Promise<void> => {
  if (current !== null) {
    if (previous === null) previouslyFocusedElement = document.activeElement as HTMLElement | null;
    inputValue.value = current.initialValue;
    await nextTick();
    (current.kind === "prompt" ? promptInput.value : cancelButton.value)?.focus();
    return;
  }
  previouslyFocusedElement?.focus();
  previouslyFocusedElement = null;
});

function cancel(): void {
  settleAppDialog(null);
}

function confirm(): void {
  const current: AppDialogRequest | null = dialog.value;
  if (current === null || !confirmationAllowed.value) return;
  settleAppDialog(current.kind === "prompt" ? inputValue.value : true);
}

function handleBackdrop(event: MouseEvent): void {
  if (event.target === event.currentTarget) cancel();
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    event.preventDefault();
    cancel();
    return;
  }
  if (event.key !== "Tab" || dialogPanel.value === null) return;
  const focusableElements: HTMLElement[] = Array.from(
    dialogPanel.value.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
    ),
  );
  const firstElement: HTMLElement | undefined = focusableElements[0];
  const lastElement: HTMLElement | undefined = focusableElements.at(-1);
  if (firstElement === undefined || lastElement === undefined) return;
  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
}

onBeforeUnmount((): void => {
  if (dialog.value !== null) settleAppDialog(null);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="dialog" class="app-dialog-backdrop" @mousedown="handleBackdrop" @keydown="handleKeydown">
      <section
        ref="dialogPanel"
        class="app-dialog"
        :class="{ 'app-dialog--danger': dialog.tone === 'danger' }"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="`app-dialog-title-${dialog.id}`"
        :aria-describedby="`app-dialog-message-${dialog.id}`"
      >
        <header class="app-dialog__header">
          <p class="eyebrow">Confirmation required</p>
          <h2 :id="`app-dialog-title-${dialog.id}`">{{ dialog.title }}</h2>
        </header>
        <p :id="`app-dialog-message-${dialog.id}`" class="app-dialog__message">{{ dialog.message }}</p>
        <label v-if="dialog.kind === 'prompt'" class="app-dialog__input">
          <span>{{ dialog.inputLabel }}</span>
          <input ref="promptInput" v-model="inputValue" type="text" autocomplete="off" @keydown.enter.prevent="confirm" />
          <small v-if="!confirmationAllowed && dialog.validationMessage" class="form-error">{{ dialog.validationMessage }}</small>
        </label>
        <footer class="app-dialog__actions">
          <button ref="cancelButton" class="button" :class="{ 'button--danger': dialog.cancelTone === 'danger' }" type="button" @click="cancel">{{ dialog.cancelLabel }}</button>
          <button class="button" :class="dialog.tone === 'danger' ? 'button--danger' : 'button--primary'" type="button" :disabled="!confirmationAllowed" @click="confirm">{{ dialog.confirmLabel }}</button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
